import { Box, Typography } from '@mui/material';
import { CheckCircle, Circle, HourglassMedium } from '@phosphor-icons/react';
import { format } from 'date-fns';
import { ActivityInstance } from '@api';
import { getChildActivityStatus, getActivityIcon } from '@/components/child';
import { useRoutines } from '@/contexts/RoutineContext';
import DeleteInstanceButton from '@/components/activity/DeleteInstanceButton';
import EditRoutineButton from '@/components/activity/EditRoutineButton';
import { getRoutineStepLabel } from '@/utils/routine';
import type { WeekTokens } from './weekTokens';

type WeekActivityRowProps = {
  instance: ActivityInstance;
  tokens: WeekTokens;
  /** Barn: visa emoji-ikon framför namnet (additiv lekfullhet). */
  showEmoji: boolean;
  /** Teen: aktivitetens stjärnvärde väntar på förälderns besked. */
  starValuePending?: boolean;
  /** Färdigöversatt etikett för väntan-markeringen (tillgänglig text + tooltip). */
  starValuePendingLabel?: string;
  onClick: () => void;
};

/**
 * WeekActivityRow — EN gemensam aktivitetsrad för alla tre personas. Fast
 * informationsordning (konsistens-kontraktet):
 *
 *   status-indikator → tid (HH:mm) → namn
 *
 * Hela raden är klickbar → /activity/:id.
 *
 * Completed = ETT språk: den enda bärande signalen är en FYLLD bock i
 * status-positionen + en lugn bakgrundston (completedBg). Vi använder medvetet
 * INTE genomstrykning/dubbla ikoner (teens tidigare överbelastning togs bort).
 *
 * Väntande stjärnvärde läggs SIST i raden, som en dämpad timglas-ikon med
 * tillgänglig etikett — informationsordningen ovan är oförändrad, och raden är för
 * tät för ännu en textrad. Den fulla texten står i dagsvyns kort.
 */
const WeekActivityRow = ({
  instance,
  tokens,
  showEmoji,
  starValuePending = false,
  starValuePendingLabel,
  onClick,
}: WeekActivityRowProps) => {
  const isDone = getChildActivityStatus(instance) === 'done';
  const { getRoutine } = useRoutines();
  // Rutinen är ETT block i veckan. Pågår den står aktuellt steg efter namnet — samma
  // "en sak i taget" som dagsvyn — annars bara rutinens namn (getRoutineStepLabel).
  const displayName = getRoutineStepLabel(instance, getRoutine(instance));

  return (
    // Krysset ligger BREDVID raden, inte inuti den: raden är en <button>, och en
    // knapp inuti en knapp är varken giltig HTML eller nåbar med tangentbord.
    // Utanför handledarläge renderar DeleteInstanceButton ingenting, och då är
    // raden exakt lika bred som förut.
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box
        component="button"
        type="button"
        onClick={onClick}
        sx={{
          flex: 1,
          minWidth: 0,
          textAlign: 'left',
          fontFamily: 'inherit',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: '8px 10px',
          borderRadius: '10px',
          bgcolor: isDone ? tokens.completedBg : 'transparent',
          border: 'none',
          transition: 'transform 0.1s ease',
          '&:active': { transform: 'scale(0.98)' },
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
            '&:active': { transform: 'none' },
          },
        }}
      >
        {/* Status-indikator — den bärande completed-signalen */}
        {isDone ? (
          <CheckCircle size={18} weight="fill" color={tokens.done} />
        ) : (
          <Circle size={18} weight="regular" color={tokens.textSoft} />
        )}

        {/* Tid */}
        <Typography
          sx={{ fontSize: '0.78rem', fontWeight: 600, color: tokens.textSoft, minWidth: 40 }}
        >
          {format(new Date(instance.startDate), 'HH:mm')}
        </Typography>

        {/* Emoji (endast barn) + namn */}
        {showEmoji && (
          <span aria-hidden style={{ fontSize: '1rem', lineHeight: 1 }}>
            {getActivityIcon(instance)}
          </span>
        )}
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontWeight: 600,
            fontSize: '0.85rem',
            color: isDone ? tokens.textSoft : tokens.text,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {displayName}
        </Typography>

        {/* Väntande stjärnvärde (teen) */}
        {starValuePending && (
          <Box
            role="img"
            aria-label={starValuePendingLabel}
            title={starValuePendingLabel}
            sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
          >
            <HourglassMedium size={14} weight="bold" color={tokens.textSoft} />
          </Box>
        )}
      </Box>
      <EditRoutineButton instance={instance} size={14} sx={{ flexShrink: 0 }} />
      <DeleteInstanceButton instance={instance} size={14} sx={{ flexShrink: 0 }} />
    </Box>
  );
};

export default WeekActivityRow;
